import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { automationApi } from '../services/automationApi';
import { 
  X, User, Mail, Lock, Save, Loader2, CheckCircle, AlertCircle, Eye, EyeOff, Camera, Trash2,
  Zap, Cloud, Link2, TestTube, Unplug, CheckCircle2, XCircle
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const ProfileSettings = ({ onClose }) => {
  const { user, updateDisplayName, updateEmail, updateUserPassword } = useAuth();
  const fileInputRef = useRef(null);
  
  // Tab state
  const [activeTab, setActiveTab] = useState('profile'); // 'profile' | 'automation'
  
  // Form states
  const [name, setName] = useState(user?.displayName || '');
  const [email, setEmail] = useState(user?.email || '');
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  
  // Profile picture states
  const [profilePicture, setProfilePicture] = useState(null);
  const [isUploadingPicture, setIsUploadingPicture] = useState(false);
  
  // UI states
  const [activeSection, setActiveSection] = useState(null); // 'name' | 'email' | 'password'
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  
  // Automation states
  const [automationSettings, setAutomationSettings] = useState(null);
  const [loadingAutomation, setLoadingAutomation] = useState(false);
  const [blueSkyHandle, setBlueSkyHandle] = useState('');
  const [blueSkyAppPassword, setBlueSkyAppPassword] = useState('');
  const [connectingBlueSky, setConnectingBlueSky] = useState(false);
  const [webhookUrl, setWebhookUrl] = useState('');
  const [webhookSecret, setWebhookSecret] = useState('');
  const [savingWebhook, setSavingWebhook] = useState(false);
  const [testingWebhook, setTestingWebhook] = useState(false);

  // Load profile picture on mount
  useEffect(() => {
    if (user?.uid) {
      loadProfilePicture();
    }
  }, [user?.uid]);

  // Load automation settings when switching to automation tab
  useEffect(() => {
    if (activeTab === 'automation' && user?.uid && !automationSettings) {
      loadAutomationSettings();
    }
  }, [activeTab, user?.uid]);

  const loadAutomationSettings = async () => {
    setLoadingAutomation(true);
    try {
      const settings = await automationApi.getSettings(user.uid);
      setAutomationSettings(settings);
      if (settings.webhook_url) {
        setWebhookUrl(settings.webhook_url);
      }
    } catch (err) {
      console.error('Failed to load automation settings:', err);
    } finally {
      setLoadingAutomation(false);
    }
  };

  const handleConnectBlueSky = async (e) => {
    e.preventDefault();
    if (!blueSkyHandle.trim() || !blueSkyAppPassword.trim()) {
      setError('Please enter your BlueSky handle and app password');
      return;
    }
    
    setConnectingBlueSky(true);
    setError('');
    
    try {
      await automationApi.connectBlueSky(user.uid, blueSkyHandle.trim(), blueSkyAppPassword.trim());
      setSuccess('BlueSky connected successfully!');
      setBlueSkyHandle('');
      setBlueSkyAppPassword('');
      loadAutomationSettings();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.message || 'Failed to connect BlueSky');
    } finally {
      setConnectingBlueSky(false);
    }
  };

  const handleDisconnectBlueSky = async () => {
    if (!window.confirm('Disconnect your BlueSky account?')) return;
    
    setConnectingBlueSky(true);
    setError('');
    
    try {
      await automationApi.disconnectBlueSky(user.uid);
      setSuccess('BlueSky disconnected');
      loadAutomationSettings();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.message || 'Failed to disconnect BlueSky');
    } finally {
      setConnectingBlueSky(false);
    }
  };

  const handleSaveWebhook = async (e) => {
    e.preventDefault();
    if (!webhookUrl.trim()) {
      setError('Please enter a webhook URL');
      return;
    }
    
    setSavingWebhook(true);
    setError('');
    
    try {
      await automationApi.configureWebhook(user.uid, webhookUrl.trim(), webhookSecret.trim() || null);
      setSuccess('Webhook configured successfully!');
      loadAutomationSettings();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.message || 'Failed to save webhook');
    } finally {
      setSavingWebhook(false);
    }
  };

  const handleTestWebhook = async () => {
    setTestingWebhook(true);
    setError('');
    
    try {
      const result = await automationApi.testWebhook(user.uid);
      if (result.success) {
        setSuccess('Webhook test successful! Check your endpoint.');
      } else {
        setError(result.error || 'Webhook test failed');
      }
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.message || 'Webhook test failed');
    } finally {
      setTestingWebhook(false);
    }
  };

  const handleRemoveWebhook = async () => {
    if (!window.confirm('Remove webhook configuration?')) return;
    
    setSavingWebhook(true);
    setError('');
    
    try {
      await automationApi.removeWebhook(user.uid);
      setSuccess('Webhook removed');
      setWebhookUrl('');
      setWebhookSecret('');
      loadAutomationSettings();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.message || 'Failed to remove webhook');
    } finally {
      setSavingWebhook(false);
    }
  };

  const loadProfilePicture = async () => {
    try {
      const response = await fetch(`${API_URL}/api/user/profile-picture/${user.uid}`);
      if (response.ok) {
        const blob = await response.blob();
        setProfilePicture(URL.createObjectURL(blob));
      }
    } catch (err) {
      // No profile picture, that's fine
    }
  };

  const handlePictureUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      setError('Please select an image file');
      return;
    }

    // Validate file size (2MB max)
    if (file.size > 2 * 1024 * 1024) {
      setError('Image must be less than 2MB');
      return;
    }

    setIsUploadingPicture(true);
    setError('');

    try {
      // Convert to base64
      const reader = new FileReader();
      reader.onloadend = async () => {
        try {
          const response = await fetch(`${API_URL}/api/user/profile-picture`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              user_id: user.uid,
              image_data: reader.result,
            }),
          });

          if (!response.ok) {
            throw new Error('Failed to upload image');
          }

          setSuccess('Profile picture updated!');
          setTimeout(() => setSuccess(''), 3000);
          loadProfilePicture();
        } catch (err) {
          setError(err.message);
        } finally {
          setIsUploadingPicture(false);
        }
      };
      reader.readAsDataURL(file);
    } catch (err) {
      setError('Failed to read image file');
      setIsUploadingPicture(false);
    }
  };

  const handleDeletePicture = async () => {
    if (!window.confirm('Delete your profile picture?')) return;

    setIsUploadingPicture(true);
    try {
      await fetch(`${API_URL}/api/user/profile-picture/${user.uid}`, {
        method: 'DELETE',
      });
      setProfilePicture(null);
      setSuccess('Profile picture removed');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError('Failed to delete profile picture');
    } finally {
      setIsUploadingPicture(false);
    }
  };

  const handleUpdateName = async (e) => {
    e.preventDefault();
    if (!name.trim()) {
      setError('Name cannot be empty');
      return;
    }
    
    setIsLoading(true);
    setError('');
    setSuccess('');
    
    try {
      await updateDisplayName(name.trim());
      setSuccess('Name updated successfully!');
      setActiveSection(null);
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      console.error(err);
      setError(getErrorMessage(err));
    } finally {
      setIsLoading(false);
    }
  };

  const handleUpdateEmail = async (e) => {
    e.preventDefault();
    if (!email.trim()) {
      setError('Email cannot be empty');
      return;
    }
    if (!currentPassword) {
      setError('Current password is required to change email');
      return;
    }
    
    setIsLoading(true);
    setError('');
    setSuccess('');
    
    try {
      await updateEmail(email.trim(), currentPassword);
      setSuccess('Email updated successfully!');
      setActiveSection(null);
      setCurrentPassword('');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      console.error(err);
      setError(getErrorMessage(err));
    } finally {
      setIsLoading(false);
    }
  };

  const handleUpdatePassword = async (e) => {
    e.preventDefault();
    if (!currentPassword) {
      setError('Current password is required');
      return;
    }
    if (newPassword.length < 6) {
      setError('New password must be at least 6 characters');
      return;
    }
    if (newPassword !== confirmPassword) {
      setError('New passwords do not match');
      return;
    }
    
    setIsLoading(true);
    setError('');
    setSuccess('');
    
    try {
      await updateUserPassword(newPassword, currentPassword);
      setSuccess('Password updated successfully!');
      setActiveSection(null);
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      console.error(err);
      setError(getErrorMessage(err));
    } finally {
      setIsLoading(false);
    }
  };

  const getErrorMessage = (err) => {
    switch (err.code) {
      case 'auth/wrong-password':
      case 'auth/invalid-credential':
        return 'Current password is incorrect';
      case 'auth/email-already-in-use':
        return 'This email is already in use';
      case 'auth/invalid-email':
        return 'Invalid email address';
      case 'auth/weak-password':
        return 'Password should be at least 6 characters';
      case 'auth/requires-recent-login':
        return 'Please log out and log back in, then try again';
      case 'auth/network-request-failed':
        return 'Network error. Please check your connection';
      default:
        return err.message || 'An error occurred. Please try again.';
    }
  };

  const cancelEdit = () => {
    setActiveSection(null);
    setError('');
    setCurrentPassword('');
    setNewPassword('');
    setConfirmPassword('');
    setName(user?.displayName || '');
    setEmail(user?.email || '');
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4" data-testid="profile-settings-modal">
      <div className="bg-slate-800 border border-slate-700 rounded-2xl w-full max-w-lg shadow-2xl max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-slate-700 sticky top-0 bg-slate-800 z-10">
          <h2 className="text-xl font-bold text-white">Settings</h2>
          <button
            onClick={onClose}
            className="p-2 text-slate-400 hover:text-white hover:bg-slate-700 rounded-lg transition-colors"
            data-testid="close-profile-settings"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Tabs */}
        <div className="flex gap-2 px-6 pt-4 border-b border-slate-700">
          <button
            onClick={() => { setActiveTab('profile'); setError(''); setSuccess(''); }}
            className={`px-4 py-2 rounded-t-lg text-sm font-medium flex items-center gap-2 transition-colors ${
              activeTab === 'profile'
                ? 'bg-slate-700 text-white border-b-2 border-indigo-500'
                : 'text-slate-400 hover:text-white hover:bg-slate-700/50'
            }`}
          >
            <User className="w-4 h-4" />
            Profile
          </button>
          <button
            onClick={() => { setActiveTab('automation'); setError(''); setSuccess(''); }}
            className={`px-4 py-2 rounded-t-lg text-sm font-medium flex items-center gap-2 transition-colors ${
              activeTab === 'automation'
                ? 'bg-slate-700 text-white border-b-2 border-indigo-500'
                : 'text-slate-400 hover:text-white hover:bg-slate-700/50'
            }`}
          >
            <Zap className="w-4 h-4" />
            Automation
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
          {/* Success Message */}
          {success && (
            <div className="p-4 bg-green-900/20 border border-green-500/50 rounded-lg flex items-center gap-3 text-green-400">
              <CheckCircle className="w-5 h-5 flex-shrink-0" />
              <span>{success}</span>
            </div>
          )}

          {/* Error Message */}
          {error && (
            <div className="p-4 bg-red-900/20 border border-red-500/50 rounded-lg flex items-center gap-3 text-red-400">
              <AlertCircle className="w-5 h-5 flex-shrink-0" />
              <span>{error}</span>
            </div>
          )}

          {/* Profile Tab Content */}
          {activeTab === 'profile' && (
            <>
              {/* Profile Picture Section */}
              <div className="flex flex-col items-center gap-4 pb-6 border-b border-slate-700">
                <div className="relative">
                  <div className="w-24 h-24 rounded-full bg-slate-700 border-2 border-slate-600 overflow-hidden flex items-center justify-center">
                    {profilePicture ? (
                      <img 
                        src={profilePicture} 
                    alt="Profile" 
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <User className="w-12 h-12 text-slate-500" />
                )}
              </div>
              {isUploadingPicture && (
                <div className="absolute inset-0 bg-black/50 rounded-full flex items-center justify-center">
                  <Loader2 className="w-6 h-6 text-white animate-spin" />
                </div>
              )}
            </div>
            <div className="flex gap-2">
              <input
                type="file"
                ref={fileInputRef}
                onChange={handlePictureUpload}
                accept="image/*"
                className="hidden"
              />
              <button
                onClick={() => fileInputRef.current?.click()}
                disabled={isUploadingPicture}
                className="px-3 py-1.5 bg-indigo-600 hover:bg-indigo-500 text-white text-sm rounded-lg flex items-center gap-1 disabled:opacity-50"
              >
                <Camera className="w-4 h-4" />
                {profilePicture ? 'Change' : 'Upload'}
              </button>
              {profilePicture && (
                <button
                  onClick={handleDeletePicture}
                  disabled={isUploadingPicture}
                  className="px-3 py-1.5 bg-red-600/20 hover:bg-red-600/30 text-red-400 text-sm rounded-lg flex items-center gap-1 disabled:opacity-50"
                >
                  <Trash2 className="w-4 h-4" />
                  Remove
                </button>
              )}
            </div>
            <p className="text-xs text-slate-500">Max 2MB, JPG/PNG/GIF/WebP</p>
          </div>

          {/* Name Section */}
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <label className="text-sm font-medium text-slate-300 flex items-center gap-2">
                <User className="w-4 h-4 text-indigo-400" />
                Display Name
              </label>
              {activeSection !== 'name' && (
                <button
                  onClick={() => { setActiveSection('name'); setError(''); }}
                  className="text-xs text-indigo-400 hover:text-indigo-300 font-medium"
                  data-testid="edit-name-btn"
                >
                  Edit
                </button>
              )}
            </div>
            
            {activeSection === 'name' ? (
              <form onSubmit={handleUpdateName} className="space-y-3">
                <input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Enter your name"
                  data-testid="name-input"
                  className="w-full bg-slate-900 border border-slate-600 rounded-lg px-4 py-3 text-white placeholder-slate-500 focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 outline-none"
                />
                <div className="flex gap-2">
                  <button
                    type="submit"
                    disabled={isLoading}
                    data-testid="save-name-btn"
                    className="px-4 py-2 bg-indigo-600 hover:bg-indigo-500 text-white font-medium rounded-lg flex items-center gap-2 disabled:opacity-50"
                  >
                    {isLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                    Save
                  </button>
                  <button
                    type="button"
                    onClick={cancelEdit}
                    className="px-4 py-2 bg-slate-700 hover:bg-slate-600 text-slate-300 font-medium rounded-lg"
                  >
                    Cancel
                  </button>
                </div>
              </form>
            ) : (
              <p className="text-white bg-slate-900/50 border border-slate-700 rounded-lg px-4 py-3">
                {user?.displayName || <span className="text-slate-500 italic">Not set</span>}
              </p>
            )}
          </div>

          {/* Email Section */}
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <label className="text-sm font-medium text-slate-300 flex items-center gap-2">
                <Mail className="w-4 h-4 text-indigo-400" />
                Email Address
              </label>
              {activeSection !== 'email' && (
                <button
                  onClick={() => { setActiveSection('email'); setError(''); }}
                  className="text-xs text-indigo-400 hover:text-indigo-300 font-medium"
                  data-testid="edit-email-btn"
                >
                  Edit
                </button>
              )}
            </div>
            
            {activeSection === 'email' ? (
              <form onSubmit={handleUpdateEmail} className="space-y-3">
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Enter new email"
                  data-testid="email-input"
                  className="w-full bg-slate-900 border border-slate-600 rounded-lg px-4 py-3 text-white placeholder-slate-500 focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 outline-none"
                />
                <div className="relative">
                  <input
                    type={showCurrentPassword ? 'text' : 'password'}
                    value={currentPassword}
                    onChange={(e) => setCurrentPassword(e.target.value)}
                    placeholder="Current password (required)"
                    data-testid="current-password-input"
                    className="w-full bg-slate-900 border border-slate-600 rounded-lg px-4 py-3 pr-12 text-white placeholder-slate-500 focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 outline-none"
                  />
                  <button
                    type="button"
                    onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-300"
                  >
                    {showCurrentPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
                <div className="flex gap-2">
                  <button
                    type="submit"
                    disabled={isLoading}
                    data-testid="save-email-btn"
                    className="px-4 py-2 bg-indigo-600 hover:bg-indigo-500 text-white font-medium rounded-lg flex items-center gap-2 disabled:opacity-50"
                  >
                    {isLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                    Save
                  </button>
                  <button
                    type="button"
                    onClick={cancelEdit}
                    className="px-4 py-2 bg-slate-700 hover:bg-slate-600 text-slate-300 font-medium rounded-lg"
                  >
                    Cancel
                  </button>
                </div>
              </form>
            ) : (
              <p className="text-white bg-slate-900/50 border border-slate-700 rounded-lg px-4 py-3">
                {user?.email}
              </p>
            )}
          </div>

          {/* Password Section */}
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <label className="text-sm font-medium text-slate-300 flex items-center gap-2">
                <Lock className="w-4 h-4 text-indigo-400" />
                Password
              </label>
              {activeSection !== 'password' && (
                <button
                  onClick={() => { setActiveSection('password'); setError(''); }}
                  className="text-xs text-indigo-400 hover:text-indigo-300 font-medium"
                  data-testid="edit-password-btn"
                >
                  Change
                </button>
              )}
            </div>
            
            {activeSection === 'password' ? (
              <form onSubmit={handleUpdatePassword} className="space-y-3">
                <div className="relative">
                  <input
                    type={showCurrentPassword ? 'text' : 'password'}
                    value={currentPassword}
                    onChange={(e) => setCurrentPassword(e.target.value)}
                    placeholder="Current password"
                    data-testid="current-password-input"
                    className="w-full bg-slate-900 border border-slate-600 rounded-lg px-4 py-3 pr-12 text-white placeholder-slate-500 focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 outline-none"
                  />
                  <button
                    type="button"
                    onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-300"
                  >
                    {showCurrentPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
                <div className="relative">
                  <input
                    type={showNewPassword ? 'text' : 'password'}
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    placeholder="New password (min 6 characters)"
                    data-testid="new-password-input"
                    className="w-full bg-slate-900 border border-slate-600 rounded-lg px-4 py-3 pr-12 text-white placeholder-slate-500 focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 outline-none"
                  />
                  <button
                    type="button"
                    onClick={() => setShowNewPassword(!showNewPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-300"
                  >
                    {showNewPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
                <input
                  type={showNewPassword ? 'text' : 'password'}
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="Confirm new password"
                  data-testid="confirm-password-input"
                  className="w-full bg-slate-900 border border-slate-600 rounded-lg px-4 py-3 text-white placeholder-slate-500 focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 outline-none"
                />
                <div className="flex gap-2">
                  <button
                    type="submit"
                    disabled={isLoading}
                    data-testid="save-password-btn"
                    className="px-4 py-2 bg-indigo-600 hover:bg-indigo-500 text-white font-medium rounded-lg flex items-center gap-2 disabled:opacity-50"
                  >
                    {isLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                    Update Password
                  </button>
                  <button
                    type="button"
                    onClick={cancelEdit}
                    className="px-4 py-2 bg-slate-700 hover:bg-slate-600 text-slate-300 font-medium rounded-lg"
                  >
                    Cancel
                  </button>
                </div>
              </form>
            ) : (
              <p className="text-slate-500 bg-slate-900/50 border border-slate-700 rounded-lg px-4 py-3">
                ••••••••
              </p>
            )}
          </div>
            </>
          )}

          {/* Automation Tab Content */}
          {activeTab === 'automation' && (
            <>
              {loadingAutomation ? (
                <div className="flex items-center justify-center py-10">
                  <Loader2 className="w-8 h-8 animate-spin text-indigo-400" />
                </div>
              ) : (
                <>
                  {/* BlueSky Connection */}
                  <div className="bg-slate-900/50 border border-slate-700 rounded-xl p-5">
                    <div className="flex items-center gap-3 mb-4">
                      <div className="p-2 bg-sky-500/20 rounded-lg">
                        <Cloud className="w-5 h-5 text-sky-400" />
                      </div>
                      <div>
                        <h3 className="font-bold text-white">BlueSky</h3>
                        <p className="text-xs text-slate-400">Post directly to BlueSky from FlowGen</p>
                      </div>
                    </div>

                    {automationSettings?.bluesky_connected ? (
                      <div className="space-y-3">
                        <div className="flex items-center gap-2 p-3 bg-green-900/20 border border-green-800/50 rounded-lg">
                          <CheckCircle2 className="w-5 h-5 text-green-400" />
                          <div>
                            <p className="text-sm font-medium text-green-400">Connected</p>
                            <p className="text-xs text-green-400/70">@{automationSettings.bluesky_handle}</p>
                          </div>
                        </div>
                        <button
                          onClick={handleDisconnectBlueSky}
                          disabled={connectingBlueSky}
                          className="w-full px-4 py-2 bg-red-600/20 hover:bg-red-600/30 text-red-400 border border-red-800/50 rounded-lg text-sm font-medium flex items-center justify-center gap-2 disabled:opacity-50"
                        >
                          {connectingBlueSky ? <Loader2 className="w-4 h-4 animate-spin" /> : <Unplug className="w-4 h-4" />}
                          Disconnect BlueSky
                        </button>
                      </div>
                    ) : (
                      <form onSubmit={handleConnectBlueSky} className="space-y-3">
                        <div>
                          <label className="text-xs text-slate-400 mb-1 block">BlueSky Handle</label>
                          <input
                            type="text"
                            value={blueSkyHandle}
                            onChange={(e) => setBlueSkyHandle(e.target.value)}
                            placeholder="yourhandle.bsky.social"
                            className="w-full bg-slate-800 border border-slate-600 rounded-lg px-3 py-2 text-white text-sm placeholder-slate-500 focus:border-sky-500 outline-none"
                          />
                        </div>
                        <div>
                          <label className="text-xs text-slate-400 mb-1 block">App Password</label>
                          <input
                            type="password"
                            value={blueSkyAppPassword}
                            onChange={(e) => setBlueSkyAppPassword(e.target.value)}
                            placeholder="xxxx-xxxx-xxxx-xxxx"
                            className="w-full bg-slate-800 border border-slate-600 rounded-lg px-3 py-2 text-white text-sm placeholder-slate-500 focus:border-sky-500 outline-none"
                          />
                          <p className="text-[10px] text-slate-500 mt-1">
                            Create an app password at Settings → Privacy → App Passwords on BlueSky
                          </p>
                        </div>
                        <button
                          type="submit"
                          disabled={connectingBlueSky}
                          className="w-full px-4 py-2 bg-sky-600 hover:bg-sky-500 text-white rounded-lg text-sm font-medium flex items-center justify-center gap-2 disabled:opacity-50"
                        >
                          {connectingBlueSky ? <Loader2 className="w-4 h-4 animate-spin" /> : <Cloud className="w-4 h-4" />}
                          Connect BlueSky
                        </button>
                      </form>
                    )}
                  </div>

                  {/* Webhook Configuration */}
                  <div className="bg-slate-900/50 border border-slate-700 rounded-xl p-5">
                    <div className="flex items-center gap-3 mb-4">
                      <div className="p-2 bg-purple-500/20 rounded-lg">
                        <Link2 className="w-5 h-5 text-purple-400" />
                      </div>
                      <div>
                        <h3 className="font-bold text-white">Webhook</h3>
                        <p className="text-xs text-slate-400">Connect to Make.com, Zapier, or custom endpoints</p>
                      </div>
                    </div>

                    {automationSettings?.webhook_enabled ? (
                      <div className="space-y-3">
                        <div className="flex items-center gap-2 p-3 bg-green-900/20 border border-green-800/50 rounded-lg">
                          <CheckCircle2 className="w-5 h-5 text-green-400" />
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-green-400">Webhook Active</p>
                            <p className="text-xs text-green-400/70 truncate">{automationSettings.webhook_url}</p>
                          </div>
                        </div>
                        <div className="flex gap-2">
                          <button
                            onClick={handleTestWebhook}
                            disabled={testingWebhook}
                            className="flex-1 px-4 py-2 bg-slate-700 hover:bg-slate-600 text-white rounded-lg text-sm font-medium flex items-center justify-center gap-2 disabled:opacity-50"
                          >
                            {testingWebhook ? <Loader2 className="w-4 h-4 animate-spin" /> : <TestTube className="w-4 h-4" />}
                            Test
                          </button>
                          <button
                            onClick={handleRemoveWebhook}
                            disabled={savingWebhook}
                            className="flex-1 px-4 py-2 bg-red-600/20 hover:bg-red-600/30 text-red-400 border border-red-800/50 rounded-lg text-sm font-medium flex items-center justify-center gap-2 disabled:opacity-50"
                          >
                            {savingWebhook ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
                            Remove
                          </button>
                        </div>
                      </div>
                    ) : (
                      <form onSubmit={handleSaveWebhook} className="space-y-3">
                        <div>
                          <label className="text-xs text-slate-400 mb-1 block">Webhook URL</label>
                          <input
                            type="url"
                            value={webhookUrl}
                            onChange={(e) => setWebhookUrl(e.target.value)}
                            placeholder="https://hook.make.com/..."
                            className="w-full bg-slate-800 border border-slate-600 rounded-lg px-3 py-2 text-white text-sm placeholder-slate-500 focus:border-purple-500 outline-none"
                          />
                        </div>
                        <div>
                          <label className="text-xs text-slate-400 mb-1 block">Secret Key (optional)</label>
                          <input
                            type="text"
                            value={webhookSecret}
                            onChange={(e) => setWebhookSecret(e.target.value)}
                            placeholder="Optional authentication secret"
                            className="w-full bg-slate-800 border border-slate-600 rounded-lg px-3 py-2 text-white text-sm placeholder-slate-500 focus:border-purple-500 outline-none"
                          />
                        </div>
                        <button
                          type="submit"
                          disabled={savingWebhook}
                          className="w-full px-4 py-2 bg-purple-600 hover:bg-purple-500 text-white rounded-lg text-sm font-medium flex items-center justify-center gap-2 disabled:opacity-50"
                        >
                          {savingWebhook ? <Loader2 className="w-4 h-4 animate-spin" /> : <Link2 className="w-4 h-4" />}
                          Save Webhook
                        </button>
                      </form>
                    )}
                  </div>

                  {/* Info Box */}
                  <div className="bg-indigo-900/20 border border-indigo-800/50 rounded-lg p-4">
                    <h4 className="text-sm font-medium text-indigo-300 mb-2">How Automation Works</h4>
                    <ul className="text-xs text-indigo-300/70 space-y-1">
                      <li>• <strong>BlueSky:</strong> Click "Post to BlueSky" on any BlueSky post to publish directly</li>
                      <li>• <strong>Webhook:</strong> Triggered automatically when you mark posts as ready</li>
                      <li>• Webhooks send post content, hashtags, image URL, and schedule time</li>
                    </ul>
                  </div>
                </>
              )}
            </>
          )}
        </div>

        {/* Footer */}
        <div className="p-6 border-t border-slate-700 bg-slate-800/50 rounded-b-2xl">
          <p className="text-xs text-slate-500 text-center">
            {activeTab === 'profile' 
              ? 'Changes to email and password require your current password for security.'
              : 'Automation settings are saved automatically.'
            }
          </p>
        </div>
      </div>
    </div>
  );
};

export default ProfileSettings;
